import { isDrawableLine, routeLinePath } from '../../../components/climbs/route-line';
import type { CragRoute } from '../../../types/crag-route';

interface SectorLinePreviewsProps {
  routes: CragRoute[];
  naturalWidth: number;
  naturalHeight: number;
  /**
   * The sector's own line thickness, so the topo previews at the weight the
   * slider is being set to rather than at whatever each route was last saved
   * with.
   */
  thickness: number;
  onSelect: (routeId: string) => void;
}

/**
 * Every line already drawn on this sector, thin and unlabelled, with nothing
 * open — the sector's topo as it stands.
 *
 * It is a way *into* the list as much as a view of it: pressing a line opens
 * that route, which is how a setter finds the one they are looking at on the
 * rock rather than by name. Only shown when no route is open — the editor draws
 * one line at a time, and the whole sector behind it would be noise: it gets the
 * two lines either side (`SectorSideLines`).
 *
 * No focus mask and no markers, unlike `RouteLineOverlay`: a dozen greyscale
 * corridors stacked on one photo cancel each other out.
 */
export function SectorLinePreviews({
  routes,
  naturalWidth,
  naturalHeight,
  thickness,
  onSelect,
}: SectorLinePreviewsProps) {
  // A line traced on some other picture — a route a member added with a photo of
  // its own — is left out rather than drawn in the wrong place on the cliff.
  const drawn = routes.filter((route) => isDrawableLine(route.line) && route.line_on_sector_photo);
  if (drawn.length === 0) return null;

  const longEdge = Math.max(naturalWidth, naturalHeight);
  const lineWidth = longEdge * thickness;

  return (
    <svg
      className="absolute inset-0 size-full"
      viewBox={`0 0 ${naturalWidth} ${naturalHeight}`}
      preserveAspectRatio="xMidYMid meet"
      data-testid="sector-line-previews"
    >
      {drawn.map((route) => {
        const d = routeLinePath(route.line!.points, naturalWidth, naturalHeight);

        return (
          <g key={route.id} className="cursor-pointer">
            {/* A fat transparent stroke under the visible one: a hairline is not
                something a pointer can reasonably be asked to hit. */}
            <path
              d={d}
              stroke="transparent"
              strokeWidth={lineWidth * 6}
              strokeLinecap="round"
              fill="none"
              // The frame beneath reads a press as "start drawing here", so a
              // press that landed on a route must not reach it.
              onPointerDown={(event) => event.stopPropagation()}
              onClick={() => onSelect(route.id)}
            >
              <title>{route.name}</title>
            </path>
            <path
              d={d}
              stroke="rgba(0,0,0,0.55)"
              strokeWidth={lineWidth * 2.4}
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
              pointerEvents="none"
            />
            <path
              d={d}
              stroke="white"
              strokeWidth={lineWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
              opacity={0.65}
              pointerEvents="none"
            />
          </g>
        );
      })}
    </svg>
  );
}
